"use client";

import Link from "next/link";
import { useMemo } from "react";
import HeroCrossfade from "@/components/dashboard/jtbd/HeroCrossfade";
import { BADGE_CLASS, getHeroProducts, type HeroProduct, type HeroProductId } from "./heroLabData";

const TILE_IDS: HeroProductId[] = ["edit-chat", "photoshoot", "photo-editor", "go-viral", "free"];

function Tile({ product }: { product: HeroProduct }) {
  return (
    <Link
      href={product.href}
      className="group relative flex flex-col justify-center rounded-2xl border border-gray-200/80 bg-white p-4 transition hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md"
    >
      <span className="flex items-center justify-between">
        <span
          className={`flex h-9 w-9 items-center justify-center rounded-xl text-base ${product.iconTile}`}
          aria-hidden
        >
          {product.icon}
        </span>
        {product.badge ? (
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${BADGE_CLASS[product.badge]}`}>
            {product.badge}
          </span>
        ) : null}
      </span>
      <span className="mt-2.5 text-sm font-bold text-gray-900 md:text-base">{product.label}</span>
      <span className="mt-0.5 line-clamp-2 text-xs leading-snug text-gray-500 md:text-[13px]">
        {product.blurb}
      </span>
    </Link>
  );
}

/** Option B - Aragon-style bento: one big money tile + a grid of tool tiles. */
export default function HeroBento() {
  const products = useMemo(() => getHeroProducts(), []);
  const headshots = products.headshots;

  return (
    <section className="mb-8 md:mb-10">
      <h1
        className="mb-5 text-3xl font-bold text-gray-900 md:text-4xl lg:text-5xl"
        style={{ fontFamily: "var(--font-merriweather)" }}
      >
        One selfie. Every version of you.
      </h1>
      <div className="grid gap-4 lg:grid-cols-12">
        <Link
          href={headshots.href}
          className="group relative min-h-[240px] overflow-hidden rounded-3xl bg-[#173A6E] lg:col-span-7 lg:min-h-[340px]"
        >
          <div className="absolute inset-y-0 right-0 w-[48%]">
            <HeroCrossfade
              images={headshots.images}
              imageClassName="inset-0 h-full w-full object-cover object-[center_20%]"
              priority
            />
            <div
              className="absolute inset-0"
              style={{ background: "linear-gradient(to right, #173A6E 0%, rgba(23,58,110,0.5) 18%, transparent 40%)" }}
            />
          </div>
          <div className="relative z-10 flex h-full max-w-[60%] flex-col justify-center p-6 md:p-8">
            <h2
              className="text-2xl font-bold leading-tight text-white md:text-[2rem]"
              style={{ fontFamily: "var(--font-merriweather)" }}
            >
              Professional Headshots
            </h2>
            <p className="mt-2 text-sm text-white/80 md:text-[15px]">
              Studio-quality headshots from one selfie. 515+ outfits, backgrounds & poses.
            </p>
            <span className="mt-5 inline-flex w-fit rounded-xl bg-amber-400 px-5 py-2.5 text-sm font-bold text-amber-950 shadow-sm transition group-hover:brightness-105">
              Create Headshots
            </span>
          </div>
        </Link>

        <div className="grid grid-cols-2 gap-3 lg:col-span-5 lg:grid-rows-3">
          {TILE_IDS.map((id) => (
            <Tile key={id} product={products[id]} />
          ))}
          <Link
            href="#retouch"
            className="flex items-center justify-center rounded-2xl border border-dashed border-gray-300 p-4 text-sm font-semibold text-gray-500 transition hover:border-primary/40 hover:text-primary"
          >
            Browse 50+ tools ↓
          </Link>
        </div>
      </div>
    </section>
  );
}
